import { useState, useEffect, useRef } from 'react';
import { getGlobalState } from '../../../../utils/globalState';
import HCServices from '../../../../services/hcServices.js';
import { STATUS, MODES, ENERGY_SOURCE, ENERGY_MODES_API_VALUES } from '../../../../utils/constant';
import { convertUnit, airRoundOff, getSubsetRange, getAirUnit } from '../../../../utils/utility';
import { getSliderMarksArray, orderMap } from '../util';
import { frameSchedulerData } from '../../IQAirScheduler/utils/helpers.js';

export const useIQAirData = (isDarkMode=false) => {
  const [loading, setLoading] = useState(true);
  const [iqAirDetails, setIqAirDetails] = useState({});
  const [availableModes, setAvailableModes] = useState(null);
  const [energySource, setEnergySource] = useState(null);
  const [IQAirRange, setIQAirRange] = useState({ min: null, max: null });
  const [IQAirTemperatureRange, setIQAirTemperatureRange] = useState({});
  const [IQAirComfortRange, setIQAirComfortRange] = useState({});
  const [isThermostatSmartModeAllowed, setIsThermostatSmartModeAllowed] = useState(false);
  const [isHemsOptScheduleSupported, setIsHemsOptScheduleSupported] = useState(false);
  const [isIQAirOnly, setIsIQAirOnly] = useState(false);
  const [headerName, setHeaderName] = useState(null);
  const [airDeviceName] = useState(getGlobalState("airDeviceName"));
  const [IQAirPoints, setIQAirPoints] = useState();
  const [marksArray, setMarksArray] = useState();
  const [range, setRange] = useState();
  const [manualProfile, setManualProfile] = useState(null);
  const [smartProfile, setSmartProfile] = useState(null);
  const [energyMode, setEnergyMode] = useState(null);
  const [presets, setPresets] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);
  const [apiError, setApiError] = useState(false);
  const siteId = getGlobalState('siteId');
  const iqAirId = getGlobalState('iqAirId');
  const refreshTimerRef = useRef(null);

  const initSiteSettings = async () => {
    try {
      const siteSettingsResponse = await HCServices.getSiteSettings(siteId);
      if (!siteSettingsResponse?.data) {
        throw new Error('Invalid site settings response');
      }
      if (siteSettingsResponse.data.showProduction === false && siteSettingsResponse.data.showConsumption === false) {
        setIsIQAirOnly(true);
      }
      if (siteSettingsResponse.data.hasEncharge === false) {
        setIsIQAirOnly(true);
      }
      if (siteSettingsResponse.data.isThermostatSmartModeAllowed) {
        setIsThermostatSmartModeAllowed(true);
      }
      if (siteSettingsResponse.data.isHemsOptScheduleSupported) {
        setIsHemsOptScheduleSupported(true);
      }
      return true;
    } catch (e) {
      console.error('error in getting site settings:', e);
      throw e;
    }
  };

  const initAirDevice = async () => {
    try {
      const response = await HCServices.getAirDeviceDetails(siteId, iqAirId);
      if (response.status === STATUS.SUCCESS && response.data && response.data[0]) {
        const deviceData = response.data[0];
        const airDeviceSetting = deviceData.settings;
        const airDeviceState = deviceData.state;
        const smartSettings = deviceData['smart-settings'];
        const normalizedAvailableModes = (deviceData['available-modes'] || [])
          .map(v => String(v).toUpperCase())
          .sort((a, b) => orderMap[a.toLowerCase()] - orderMap[b.toLowerCase()]);
        const hasHeatSupport = normalizedAvailableModes.includes(MODES.HEAT);
        const hasCoolSupport = normalizedAvailableModes.includes(MODES.COOL);

        setAvailableModes(
          normalizedAvailableModes
        );
        setEnergySource(deviceData['energy-source']);

        if (airDeviceSetting) {
          const currentEnergyMode = airDeviceSetting.mode;
          const isSmartEnergyMode = currentEnergyMode === ENERGY_MODES_API_VALUES.SMART;
          const heatSetpointRange = airDeviceSetting['heat-setpoint-range'];
          const coolSetpointRange = airDeviceSetting['cool-setpoint-range'];
          const hasValidRange = (setpointRange) => {
            if (!setpointRange || typeof setpointRange !== 'object') return false;
            const min = Number(setpointRange.min);
            const max = Number(setpointRange.max);
            return Number.isFinite(min) && Number.isFinite(max);
          };
          const hasHeatRange = hasValidRange(heatSetpointRange);
          const hasCoolRange = hasValidRange(coolSetpointRange);
          const primaryRange = hasHeatRange
            ? heatSetpointRange
            : (hasCoolRange ? coolSetpointRange : null);
          const secondaryRange = hasCoolRange
            ? coolSetpointRange
            : (hasHeatRange ? heatSetpointRange : null);

          setEnergyMode(currentEnergyMode);

          const iqAirDetailsObj = {
            name: deviceData.name,
            airMode: airDeviceSetting["temperature-mode"],
            fanMode: airDeviceSetting["fan-mode"],
            state: airDeviceState,
          };
          
          setIqAirDetails({ ...iqAirDetailsObj });
          setHeaderName(iqAirDetailsObj.name);
          setIQAirTemperatureRange({
            'heat-setpoint-range': hasHeatSupport && hasHeatRange ? heatSetpointRange : null,
            'cool-setpoint-range': hasCoolSupport && hasCoolRange ? coolSetpointRange : null
          });
          setIQAirRange(getSubsetRange(
            primaryRange,
            secondaryRange,
            true
          ));

          const rangeOfSetpoints = getSubsetRange(
            primaryRange,
            secondaryRange,
            false
          );

          const resolveSetpoint = (setpointKey, source) => {
            const sourceValue = Number(source?.[setpointKey]);
            const fallbackValue = Number(airDeviceSetting?.[setpointKey]);
            const minBound = Number(rangeOfSetpoints?.min);
            const maxBound = Number(rangeOfSetpoints?.max);

            const rawValue = Number.isFinite(sourceValue)
              ? sourceValue
              : (Number.isFinite(fallbackValue) ? fallbackValue : minBound);

            if (!Number.isFinite(rawValue)) {
              return null;
            }

            if (!Number.isFinite(minBound) || !Number.isFinite(maxBound)) {
              return airRoundOff(convertUnit(rawValue));
            }

            const boundedValue = Math.min(Math.max(rawValue, minBound), maxBound);
            return airRoundOff(convertUnit(boundedValue));
          };

          const buildProfileFromSource = (source) => {
            const setPoints = {
              heat_setpoint: resolveSetpoint("heat-setpoint", source),
              cool_setpoint: resolveSetpoint("cool-setpoint", source),
            };

            const rawCoolComfortRange = source?.["cool-comfort-range"] || airDeviceSetting?.["cool-comfort-range"];
            const rawHeatComfortRange = source?.["heat-comfort-range"] || airDeviceSetting?.["heat-comfort-range"];

            const rawCoolLower = Number(rawCoolComfortRange && rawCoolComfortRange.lower);
            const rawCoolUpper = Number(rawCoolComfortRange && rawCoolComfortRange.upper);
            const rawHeatLower = Number(rawHeatComfortRange && rawHeatComfortRange.lower);
            const rawHeatUpper = Number(rawHeatComfortRange && rawHeatComfortRange.upper);

            const cool_comfort_range = {
              lower: Number.isFinite(rawCoolLower) ? rawCoolLower : 0,
              upper: Number.isFinite(rawCoolUpper) ? rawCoolUpper : 0,
            };
            const heat_comfort_range = {
              lower: Number.isFinite(rawHeatLower) ? rawHeatLower : 0,
              upper: Number.isFinite(rawHeatUpper) ? rawHeatUpper : 0,
            };

            const sliderMarkArrCool = getSliderMarksArray(setPoints.cool_setpoint);
            const sliderMarkArrHeat = getSliderMarksArray(setPoints.heat_setpoint);
            const multipleOfComfortRange = getAirUnit() === 'F' ? 2 : 1;

            const rangeValCool = [
              (setPoints.cool_setpoint) - (multipleOfComfortRange * cool_comfort_range.lower),
              (setPoints.cool_setpoint) + (multipleOfComfortRange * cool_comfort_range.upper)
            ];
            const rangeValHeat = [
              (setPoints.heat_setpoint) - (multipleOfComfortRange * heat_comfort_range.lower),
              (setPoints.heat_setpoint) + (multipleOfComfortRange * heat_comfort_range.upper)
            ];

            return {
              setPoints,
              comfortRange: {
                cool_comfort_range,
                heat_comfort_range
              },
              marksArray: { heat_setpoint: sliderMarkArrHeat, cool_setpoint: sliderMarkArrCool },
              range: { heat_setpoint: rangeValHeat, cool_setpoint: rangeValCool }
            };
          };

          const manualSource = airDeviceSetting;
          const smartSource = smartSettings || airDeviceSetting;

          const manualProfileData = buildProfileFromSource(manualSource);
          const smartProfileData = buildProfileFromSource(smartSource);
          const activeProfile = isSmartEnergyMode ? smartProfileData : manualProfileData;

          setManualProfile(manualProfileData);
          setSmartProfile(smartProfileData);

          // Set state for the main component
          setIQAirPoints(activeProfile.setPoints);
          setIQAirComfortRange(activeProfile.comfortRange);
          setMarksArray(activeProfile.marksArray);
          setRange(activeProfile.range);
          setPresets(Array.isArray(deviceData?.presets) ? deviceData.presets : []);

          const customSchedules = deviceData?.['custom-schedules'];
          if (customSchedules && typeof customSchedules === 'object' && !Array.isArray(customSchedules)) {
            const normalizedSchedules = {
              monday: Array.isArray(customSchedules.monday) ? customSchedules.monday : [],
              tuesday: Array.isArray(customSchedules.tuesday) ? customSchedules.tuesday : [],
              wednesday: Array.isArray(customSchedules.wednesday) ? customSchedules.wednesday : [],
              thursday: Array.isArray(customSchedules.thursday) ? customSchedules.thursday : [],
              friday: Array.isArray(customSchedules.friday) ? customSchedules.friday : [],
              saturday: Array.isArray(customSchedules.saturday) ? customSchedules.saturday : [],
              sunday: Array.isArray(customSchedules.sunday) ? customSchedules.sunday : [],
            };
            setScheduleData(frameSchedulerData(normalizedSchedules, isDarkMode));
          } else {
            setScheduleData(null);
          }

          return true;
        }
      }
      throw new Error('Invalid air device details response');
    } catch (error) {
      console.error('initAirDevice error:', error);
      throw error;
    }
  };

  const initializeData = async () => {
    setLoading(true);
    setApiError(false);

    try {
      await Promise.all([
        initAirDevice(),
        initSiteSettings()
      ]);

      setApiError(false);
    } catch (error) {
      console.log('One or more APIs failed:', error);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeData();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  return {
    loading,
    iqAirDetails,
    setIqAirDetails,
    availableModes,
    energySource,
    IQAirRange,
    IQAirTemperatureRange,
    IQAirComfortRange,
    setIQAirComfortRange,
    isThermostatSmartModeAllowed,
    isHemsOptScheduleSupported,
    isIQAirOnly,
    headerName,
    setHeaderName,
    airDeviceName,
    siteId,
    iqAirId,
    IQAirPoints,
    setIQAirPoints,
    marksArray,
    setMarksArray,
    range,
    setRange,
    manualProfile,
    smartProfile,
    energyMode,
    setEnergyMode,
    presets,
    setPresets,
    apiError,
    scheduleData,
    setScheduleData,
    initializeData
  };
};
